// Considered: RollupRosterCoverage footer sub-parts are plain text; the exclusions list needs a collapsible
// header with a body, which is CollapsibleSectionAtom as-is. See spec Reuse Audit.
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'localization';
import CollapsibleSectionAtom from '../../atoms/CollapsibleSection/CollapsibleSectionAtom';
import ButtonAtom from '../../atoms/Button/ButtonAtom';
import GlyphAtom from '../../atoms/Glyph/GlyphAtom';
import { useMultipleUserData } from '../../../hooks/useUserData';
import { useIdentitySubjectSpheres } from '../../../hooks/useIdentitySubjectSpheres';
import { useSpherePermission } from '../../../hooks/useSpherePermission';
import type { RosterExclusion, RosterExclusions } from '../../../types/statusFactors';

export interface RollupRosterExclusionsProps {
  /** The aggregate sphere; Restore is offered only to callers who can edit it. */
  sphereId: string;
  exclusions: RosterExclusions | undefined;
  /** Lifts one exclusion. Rejects when the lift failed; the caller reports it. */
  onRestore: (item: RosterExclusion, displayName: string) => Promise<void>;
}

const MARKER = 'data-exclusion-restore';

const formatDate = (iso: string, language: string | undefined): string => {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  const withYear = date.getFullYear() !== new Date().getFullYear();
  return new Intl.DateTimeFormat(language, {
    month: 'short',
    day: 'numeric',
    ...(withYear ? { year: 'numeric' } : {}),
  }).format(date);
};

/**
 * The roster footer's author-exclusions list: collapsed to one readable line, expanded to who, when and
 * why per exclusion. The count is the engine's total, so it holds when the caller cannot see every item.
 */
const RollupRosterExclusions: React.FC<RollupRosterExclusionsProps> = ({ sphereId, exclusions, onRestore }) => {
  const { t, i18n } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const [restoringId, setRestoringId] = useState<string | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const refocus = useRef(false);
  const { allowed } = useSpherePermission(sphereId, 'edit');

  const items = exclusions?.items ?? [];
  // `excludedBy` is an identity id; names live on the identity's subject sphere.
  const sphereByIdentity = useIdentitySubjectSpheres(items.map((item) => item.excludedBy));
  const people = useMultipleUserData(Array.from(new Set(Object.values(sphereByIdentity))));

  // The restored row unmounts with the focus on its button: move to the next Restore, else the header.
  useEffect(() => {
    if (!refocus.current) return;
    refocus.current = false;
    const next = rootRef.current?.querySelector<HTMLElement>(`[${MARKER}] button`);
    (next ?? rootRef.current?.querySelector<HTMLElement>('[role="button"]'))?.focus();
  }, [exclusions]);

  if (!exclusions || exclusions.total <= 0) return null;

  const language = i18n?.language;
  const whoOf = (item: RosterExclusion): string | undefined => {
    const subjectSphereId = sphereByIdentity[item.excludedBy];
    return (subjectSphereId && people[subjectSphereId]?.info?.sphere?.properties?.name) || undefined;
  };
  const nameOf = (item: RosterExclusion): string =>
    item.hidden || !item.sphereName
      ? t('statusFactors.roster.exclusions.hiddenName', "a sphere you can't see")
      : item.sphereName;
  const reasonOf = (item: RosterExclusion): string | null => {
    if (item.hidden) return null;
    if (item.reason) return `“${item.reason}”`;
    return t('statusFactors.roster.exclusions.noReason', 'no reason given');
  };

  const byLine = (item: RosterExclusion): string => {
    const who = whoOf(item);
    const when = formatDate(item.excludedAt, language);
    if (who) {
      return when
        ? t('statusFactors.roster.exclusions.byWhoWhen', 'excluded by {{who}} · {{when}}', { who, when })
        : t('statusFactors.roster.exclusions.byWho', 'excluded by {{who}}', { who });
    }
    return when
      ? t('statusFactors.roster.exclusions.byWhen', 'excluded · {{when}}', { when })
      : t('statusFactors.roster.exclusions.excludedOnly', 'excluded');
  };

  const latest = items[0];
  const count = exclusions.total;
  let summary = t('statusFactors.roster.exclusions.summaryCount', '{{count}} excluded', { count });
  if (latest) {
    const latestText = `${nameOf(latest)} — ${byLine(latest)}`;
    summary =
      count === 1
        ? t('statusFactors.roster.exclusions.summaryOne', '{{count}} excluded · {{latest}}', { count, latest: latestText })
        : t('statusFactors.roster.exclusions.summaryMany', '{{count}} excluded · latest: {{latest}}', {
            count,
            latest: latestText,
          });
  }

  const restore = async (item: RosterExclusion) => {
    setRestoringId(item.sphereId);
    try {
      await onRestore(item, nameOf(item));
      refocus.current = true;
    } catch {
      // The molecule reports the failure in its strip; the row stays so Restore can be retried.
    } finally {
      setRestoringId(null);
    }
  };

  return (
    <div ref={rootRef} className="rollup-roster__exclusions" data-testid="roster-exclusions">
      <CollapsibleSectionAtom
        title={summary}
        expanded={expanded}
        onExpandedChange={setExpanded}
        className="rollup-roster__exclusions-section"
        data-testid="roster-exclusions-section"
        icon="lucide:circle-slash"
      >
        <div className="rollup-roster__exclusions-body">
          {items.length > 0 && (
            <ul className="rollup-roster__exclusions-list" data-testid="roster-exclusions-list">
              {items.map((item) => {
                const name = nameOf(item);
                const busy = restoringId === item.sphereId;
                return (
                  <li key={item.sphereId} className="rollup-roster__exclusion" data-testid="roster-exclusion" aria-busy={busy}>
                    <GlyphAtom glyph="lucide:circle-slash" className="rollup-roster__exclusion-glyph" aria-hidden />
                    <div className="rollup-roster__exclusion-text">
                      <span className="rollup-roster__exclusion-name">{name}</span>
                      <span className="rollup-roster__exclusion-meta">{byLine(item)}</span>
                      <span className="rollup-roster__exclusion-reason" data-testid="roster-exclusion-reason">
                        {reasonOf(item)}
                      </span>
                    </div>
                    {allowed && (
                      <span {...{ [MARKER]: '' }}>
                        <ButtonAtom
                          variant="text"
                          size="small"
                          label={t('statusFactors.roster.exclusions.restore', 'Restore')}
                          aria-label={t('statusFactors.roster.exclusions.restoreNamed', 'Restore {{name}}', { name })}
                          loading={busy}
                          disabled={restoringId !== null}
                          onClick={() => void restore(item)}
                        />
                      </span>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
          {exclusions.omittedCount > 0 && (
            <p className="rollup-roster__exclusions-omitted" data-testid="roster-exclusions-omitted">
              {t('statusFactors.roster.exclusions.omitted', "{{count}} more you can't see", { count: exclusions.omittedCount })}
            </p>
          )}
          <p className="rollup-roster__exclusions-note" data-testid="roster-exclusions-note">
            {t(
              'statusFactors.roster.exclusions.note',
              'A member can be missing from the status for four reasons.'
            )}
          </p>
          <ul className="rollup-roster__exclusions-absences">
            <li>
              <strong>{t('statusFactors.roster.exclusions.absence.unmeasuredTerm', 'not yet measured')}</strong>{' '}
              {t('statusFactors.roster.exclusions.absence.unmeasured', '— it has no status yet, so it adds nothing until one is reported.')}
            </li>
            <li>
              <strong>{t('statusFactors.roster.exclusions.absence.hiddenTerm', 'status hidden')}</strong>{' '}
              {t('statusFactors.roster.exclusions.absence.hidden', "— you can't see the member, but its status still counts.")}
            </li>
            <li>
              <strong>{t('statusFactors.roster.exclusions.absence.excludedTerm', 'excluded')}</strong>{' '}
              {t('statusFactors.roster.exclusions.absence.excluded', '— someone removed it from this rollup by hand; it never counts.')}
            </li>
            <li>
              <strong>{t('statusFactors.roster.exclusions.absence.weightZeroTerm', 'weight 0')}</strong>{' '}
              {t('statusFactors.roster.exclusions.absence.weightZero', '— it is listed and measured but carries no weight in the result.')}
            </li>
          </ul>
        </div>
      </CollapsibleSectionAtom>
    </div>
  );
};

export default RollupRosterExclusions;
